import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Container } from '@/components/common/container';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { KeenIcon } from '@/components/keenicons';
import { useRole, useDeleteRole } from '@/hooks/admin/useRoles';
import { useRolePermissions } from '@/hooks/admin/usePermissions';
import { RoleDetailsOverview } from './components/RoleDetailsOverview';
import { RolePermissionsTab } from './components/RolePermissionsTab';
import { RoleUsersTab } from './components/RoleUsersTab';
import { toast } from 'sonner';

type TabType = 'details' | 'permissions' | 'users';

/**
 * Role Details Page
 *
 * Route: /auth/roles/:id
 * Access: Admins with Identity.Roles.Read permission
 *
 * Features:
 * - View role details and metadata
 * - Edit role information
 * - Manage role permissions
 * - View users assigned to role
 * - Delete role (with confirmation)
 *
 * Spec reference: Lines 260-444 in 03-AUTH-AUTHORIZATION-SPECS.md
 */
export function RoleDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [isEditMode, setIsEditMode] = useState(false);

  const { data: role, isLoading, error } = useRole(id!);
  // Only fetch role permissions when we have a valid role name
  // Pass undefined instead of empty string to prevent unnecessary API calls
  const { data: rolePermissions } = useRolePermissions(role?.name ?? '', {
    enabled: !!role?.name && role.name.trim().length > 0,
  });
  const deleteRoleMutation = useDeleteRole();

  // Handle URL hash for direct tab navigation
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'permissions' || hash === 'users') {
      setActiveTab(hash as TabType);
    }
  }, []);

  // Check for edit mode from query params
  useEffect(() => {
    if (searchParams.get('edit') === 'true') {
      setIsEditMode(true);
    }
  }, [searchParams]);

  const handleDelete = async () => {
    if (!role) return;

    if (role.isStatic) {
      toast.error('Cannot delete system role', {
        description: `The "${role.name}" role is a system role and cannot be deleted.`,
      });
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete the role "${role.name}"?\n\nThis will affect all users assigned to this role. This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await deleteRoleMutation.mutateAsync(role.id);
      toast.success('Role deleted successfully', {
        description: `The role "${role.name}" has been removed.`,
      });
      navigate('/auth/roles');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
      toast.error('Failed to delete role', {
        description: message,
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Container>
        <div className="flex flex-col gap-6 py-6">
          <div className="space-y-4">
            <div className="h-8 w-64 animate-pulse rounded bg-muted" />
            <div className="h-4 w-96 animate-pulse rounded bg-muted" />
          </div>
          <Card>
            <CardContent className="p-12">
              <div className="flex flex-col items-center justify-center gap-4">
                <KeenIcon icon="arrows-circle" className="animate-spin text-4xl text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Loading role details...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </Container>
    );
  }

  // Error state
  if (error || !role) {
    return (
      <Container>
        <div className="flex flex-col gap-6 py-6">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <KeenIcon icon="information-circle" className="mb-4 text-4xl text-destructive" />
              <h3 className="mb-2 text-lg font-semibold">Role not found</h3>
              <p className="mb-4 text-sm text-muted-foreground">
                {error?.message || 'The requested role could not be found.'}
              </p>
              <Button onClick={() => navigate('/auth/roles')} className="gap-2">
                <KeenIcon icon="arrow-left" />
                Back to Roles
              </Button>
            </CardContent>
          </Card>
        </div>
      </Container>
    );
  }

  const descriptionValue = role.extraProperties?.description;
  const roleDescription = typeof descriptionValue === 'string' ? descriptionValue : '';

  return (
    <Container>
      <div className="flex flex-col gap-6 py-6">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">
            Home
          </Link>
          <KeenIcon icon="right" className="text-xs" />
          <Link to="/auth/roles" className="hover:text-foreground">
            Roles
          </Link>
          <KeenIcon icon="right" className="text-xs" />
          <span className="text-foreground">{role.name}</span>
        </nav>

        {/* Page Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{role.name}</h1>
              {role.isStatic && (
                <Badge variant="secondary">
                  <KeenIcon icon="shield-tick" className="mr-1 text-xs" />
                  System Role
                </Badge>
              )}
              {role.isDefault && (
                <Badge variant="outline">
                  <KeenIcon icon="star" className="mr-1 text-xs" />
                  Default
                </Badge>
              )}
            </div>
            {roleDescription && (
              <p className="mt-2 text-muted-foreground">
                {roleDescription}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {!isEditMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIsEditMode(true)}
                  className="gap-2"
                >
                  <KeenIcon icon="pencil" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={role.isStatic || deleteRoleMutation.isPending}
                  className="gap-2"
                >
                  {deleteRoleMutation.isPending ? (
                    <KeenIcon icon="arrows-circle" className="animate-spin" />
                  ) : (
                    <KeenIcon icon="trash" />
                  )}
                  Delete
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={() => setIsEditMode(false)}
                className="gap-2"
              >
                <KeenIcon icon="cross" />
                Cancel Edit
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabType)}>
          <TabsList className="grid w-full grid-cols-3 lg:w-auto">
            <TabsTrigger value="details" className="gap-2">
              <KeenIcon icon="information" className="text-base" />
              <span className="hidden sm:inline">Role Details</span>
              <span className="sm:hidden">Details</span>
            </TabsTrigger>
            <TabsTrigger value="permissions" className="gap-2">
              <KeenIcon icon="shield-tick" className="text-base" />
              <span className="hidden sm:inline">Permissions</span>
              <span className="sm:hidden">Perms</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <KeenIcon icon="profile-circle" className="text-base" />
              <span className="hidden sm:inline">Users</span>
              <span className="sm:hidden">Users</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-6">
            <RoleDetailsOverview role={role} isEditMode={isEditMode} onEditComplete={() => setIsEditMode(false)} />
          </TabsContent>

          <TabsContent value="permissions" className="mt-6">
            <RolePermissionsTab
              role={role}
              rolePermissions={rolePermissions}
              isEditMode={isEditMode}
            />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <RoleUsersTab roleId={role.id} roleName={role.name} />
          </TabsContent>
        </Tabs>
      </div>
    </Container>
  );
}
